/* eslint-disable node/no-process-env */
import { isRunningInIOSWebview } from "../utils/environment";

/**
 * Automatic sign-in (code-xbh.21.7): a signed-out page load on develop tries
 * the Ideaflow ID session once per browser session with prompt=none. These are
 * the guards that decide whether to try; DevelopSession does the hop.
 */

// One attempt per browser session: a session cookie (no Max-Age/Expires), so
// new tabs share it. Never cleared by a login_required answer.
const markerName = "ideaflow_auto_signin";

// Crawlers, link unfurlers and scripted clients (shared across Ideaflow apps).
// HeadlessChrome and navigator.webdriver are deliberately not blocked.
const botUserAgent =
  /bot|crawl|spider|slurp|facebookexternalhit|facebookcatalog|embedly|quora link preview|outbrain|pinterest|vkshare|w3c_validator|whatsapp|telegram|discord|slack|skype|twitter|linkedin|preview|lighthouse|inspectiontool|ahrefs|semrush|mj12|yandex|baidu|duckduck|applebot|petalbot|bytespider|gptbot|claude|perplexity|ccbot|python|curl|wget|go-http|node-fetch|axios|okhttp|java\//i;

// In-app browsers, web views and native shells keep their own sign-in flows.
// IdeaflowAndroid is the token android/ (MainActivity.kt) appends to its web view.
const embeddedUserAgent =
  /FBAN|FBAV|FB_IAB|Instagram|Line\/|Twitter|LinkedInApp|Snapchat|; wv\)|WebView|Electron|IdeaflowAndroid/i;

// Message handlers the iOS wrapper (native-ios/) registers on its WKWebView.
const iosWrapperHandlers = ["storage", "appReady", "recordAudio", "clipboard", "getMemoryUsage"];

// Routes that are themselves part of signing in or out.
const authRoutes = ["/ideaflow-id"];

/** Kill switch: NEXT_PUBLIC_DEVELOP_IDEAFLOW_AUTO_SIGNIN=false turns it off (default on). */
function autoSignInEnabled() {
  return process.env.NEXT_PUBLIC_DEVELOP_IDEAFLOW_AUTO_SIGNIN !== "false";
}

function hasMarker() {
  return document.cookie.split(";").some((part) => part.trim().startsWith(`${markerName}=`));
}

/** Confirm the browser kept the marker before leaving; blocked cookies must
 * never turn a failed silent attempt into a redirect loop. */
export function markAutoSignInAttempt(): boolean {
  document.cookie = autoSignInMarkerCookie();
  return hasMarker();
}

/** The cookie string that records this browser session's attempt. */
export function autoSignInMarkerCookie() {
  const secure = window.location.protocol === "https:" ? "; Secure" : "";
  return `${markerName}=1; Path=/; SameSite=Lax${secure}`;
}

function isNativeShell() {
  if (isRunningInIOSWebview) return true;
  if (window.location.protocol !== "https:" && window.location.protocol !== "http:") return true;
  const w = window as unknown as {
    webkit?: { messageHandlers?: Record<string, unknown> };
    ReactNativeWebView?: unknown;
    Capacitor?: unknown;
    electron?: unknown;
    ipcRenderer?: unknown;
    process?: { versions?: { electron?: string } };
  };
  const handlers = w.webkit?.messageHandlers;
  if (handlers && iosWrapperHandlers.some((name) => handlers[name])) return true;
  return !!(w.ReactNativeWebView || w.Capacitor || w.electron || w.ipcRenderer || w.process?.versions?.electron);
}

/**
 * Whether this page load may try automatic sign-in. `chooseAccountKey` is the
 * localStorage marker an explicit sign-out sets (next sign-in shows the
 * provider's chooser); while it is set, never sign in automatically.
 */
export function shouldAttemptAutoSignIn(chooseAccountKey: string): boolean {
  if (typeof window === "undefined" || !autoSignInEnabled()) return false;
  try {
    if (window.self !== window.top) return false;
  } catch {
    return false;
  }
  if (isNativeShell()) return false;
  if ((document as Document & { prerendering?: boolean }).prerendering === true) return false;
  const userAgent = navigator.userAgent || "";
  if (botUserAgent.test(userAgent) || embeddedUserAgent.test(userAgent)) return false;
  if (
    authRoutes.some((route) => window.location.pathname === route || window.location.pathname.startsWith(`${route}/`))
  )
    return false;
  if (window.localStorage.getItem(chooseAccountKey) === "1") return false;
  return !hasMarker();
}

/**
 * A same-origin path (with query and hash) to return to after sign-in, or null.
 * Anything absolute, protocol-relative or pointing at the return page is refused.
 */
export function safeReturnPath(value: string | null | undefined): string | null {
  if (typeof value !== "string" || !value.startsWith("/") || value.startsWith("//") || value.startsWith("/\\"))
    return null;
  try {
    const url = new URL(value, window.location.origin);
    if (url.origin !== window.location.origin) return null;
    if (authRoutes.some((route) => url.pathname === route || url.pathname.startsWith(`${route}/`))) return null;
    return url.pathname + url.search + url.hash;
  } catch {
    return null;
  }
}
